import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

// URL slug → backend storage key
const MAP = {
  hakkimizda: "about",
  kunye: "kunye",
  reklam: "ad_info",
  iletisim: "iletisim",
  "yayin-ilkeleri": "publishing_principles",
  kvkk: "kvkk",
  gizlilik: "privacy",
  cerez: "cookie",
  "kullanim-sartlari": "terms",
};

// Nicely formatted labels — used as a fallback when the admin hasn't set
// an explicit title for the page yet.
const LABEL = {
  hakkimizda: "Hakkımızda",
  kunye: "Künye",
  reklam: "Reklam",
  iletisim: "İletişim",
  "yayin-ilkeleri": "Yayın İlkeleri",
  kvkk: "KVKK Aydınlatma Metni",
  gizlilik: "Gizlilik Politikası",
  cerez: "Çerez Politikası",
  "kullanim-sartlari": "Kullanım Şartları",
};

const DEFAULT_CONTACT = {
  company_name: "SİNE81",
  email: "info@sine81.com",
  phone: "+90 212 000 00 00",
  address: "İstanbul, Türkiye",
  map_embed_url: "",
  working_hours: "",
};

export default function Corporate() {
  const { page } = useParams();
  const key = MAP[page];
  const [data, setData] = useState(null);
  const [ads, setAds] = useState(null);
  const [contact, setContact] = useState(DEFAULT_CONTACT);

  useEffect(() => {
    if (key) {
      api.get(`/legal/${key}`).then((r) => setData(r.data)).catch(() => setData({ title: "", content: "" }));
    }
    if (page === "reklam") {
      api.get("/ad-zones").then((r) => setAds(r.data)).catch(() => {});
    }
    if (page === "iletisim") {
      api
        .get("/settings/contact_info")
        .then((r) => {
          const v = r.data?.value;
          if (v && typeof v === "object") setContact({ ...DEFAULT_CONTACT, ...v });
        })
        .catch(() => {});
    }
  }, [page, key]);

  if (page === "iletisim") {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6" data-testid="corporate-iletisim">
        <h1 className="text-2xl md:text-3xl font-black mb-3">{data?.title || "İletişim"}</h1>

        {data?.content && (
          <div className="bg-white border border-gray-100 rounded-lg p-5 mb-5 whitespace-pre-wrap text-sm text-gray-800">
            {data.content}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          <ContactForm />

          <div className="space-y-4">
            <div className="bg-white border border-gray-100 rounded-lg p-5 space-y-3 text-sm">
              <div className="font-bold text-lg" data-testid="contact-company">
                {contact.company_name}
              </div>
              {contact.email && (
                <div className="flex items-center gap-2 text-gray-700" data-testid="contact-email">
                  <Mail className="w-4 h-4 text-[#e11d2a]" />
                  <a href={`mailto:${contact.email}`} className="hover:text-[#e11d2a]">
                    {contact.email}
                  </a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2 text-gray-700" data-testid="contact-phone">
                  <Phone className="w-4 h-4 text-[#e11d2a]" />
                  <a href={`tel:${contact.phone.replace(/\s/g, "")}`} className="hover:text-[#e11d2a]">
                    {contact.phone}
                  </a>
                </div>
              )}
              {contact.address && (
                <div className="flex items-start gap-2 text-gray-700" data-testid="contact-address">
                  <MapPin className="w-4 h-4 text-[#e11d2a] mt-0.5 shrink-0" />
                  <span className="whitespace-pre-wrap">{contact.address}</span>
                </div>
              )}
              {contact.working_hours && (
                <div className="flex items-center gap-2 text-gray-700" data-testid="contact-hours">
                  <Clock className="w-4 h-4 text-[#e11d2a]" />
                  <span>{contact.working_hours}</span>
                </div>
              )}
            </div>

            {contact.map_embed_url && (
              <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
                <iframe
                  src={contact.map_embed_url}
                  title="Konum Haritası"
                  className="w-full h-64 border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="corporate-page">
      <h1 className="text-2xl md:text-3xl font-black mb-3">
        {data?.title || LABEL[page] || page}
      </h1>
      {(data?.updated_at || data?.updated_by) && (
        <div className="flex items-center gap-1 text-[11px] text-gray-400 mb-3" data-testid="corporate-updated-at">
          <span>Son güncelleme:</span>
          {data?.updated_at && (
            <span className="font-medium text-gray-500">
              {new Date(data.updated_at).toLocaleString("tr-TR", {
                day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
              })}
            </span>
          )}
          {data?.updated_by?.name && (
            <>
              <span>•</span>
              <span className="font-medium text-gray-500">
                {data.updated_by.name}
                {data.updated_by.role === "super_admin" && " (Süper Admin)"}
                {data.updated_by.role === "province_admin" && " (İl Yöneticisi)"}
                {data.updated_by.role === "editor" && " (Editör)"}
              </span>
            </>
          )}
        </div>
      )}
      <div className="prose max-w-none whitespace-pre-wrap text-gray-800" data-testid="corporate-content">
        {data?.content || (
          <div className="text-gray-400 text-sm italic">
            Bu sayfa için henüz içerik girilmedi. Yönetici Panel → Sayfa ve Footer
            Yönetimi bölümünden düzenleyebilirsiniz.
          </div>
        )}
      </div>

      {page === "reklam" && ads && (
        <div className="mt-6">
          <h2 className="text-xl font-bold mb-3">Reklam Alanları</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {ads.map((z) => (
              <div key={z.id} className="bg-white border border-gray-100 rounded-lg p-4">
                <div className="font-bold">{z.name}</div>
                <div className="text-xs text-gray-500">{z.page_position}</div>
                <div className="mt-2 text-sm">
                  {z.width}×{z.height} px (Mobil: {z.mobile_width}×{z.mobile_height})
                </div>
                <div className="mt-2 text-sm">
                  Günlük: <strong>{z.daily_price} ₺</strong> • Haftalık:{" "}
                  <strong>{z.weekly_price} ₺</strong> • Aylık:{" "}
                  <strong>{z.monthly_price} ₺</strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      return toast.error("Ad ve mesaj zorunlu");
    }
    setSending(true);
    try {
      await api.post("/contact-form", form);
      toast.success("Mesajınız iletildi. Teşekkürler.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gönderilemedi");
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={send}
      className="bg-white border border-gray-100 rounded-lg p-5 grid gap-3"
      data-testid="contact-form"
    >
      <input
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Ad Soyad *"
        className="px-3 py-2 border border-gray-200 rounded-md text-sm"
        required
      />
      <input
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        type="email"
        placeholder="Email"
        className="px-3 py-2 border border-gray-200 rounded-md text-sm"
      />
      <input
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        placeholder="Telefon"
        className="px-3 py-2 border border-gray-200 rounded-md text-sm"
      />
      <input
        value={form.subject}
        onChange={(e) => setForm({ ...form, subject: e.target.value })}
        placeholder="Konu"
        className="px-3 py-2 border border-gray-200 rounded-md text-sm"
      />
      <textarea
        value={form.message}
        onChange={(e) => setForm({ ...form, message: e.target.value })}
        rows={4}
        placeholder="Mesaj *"
        className="px-3 py-2 border border-gray-200 rounded-md text-sm"
        required
      />
      <button
        type="submit"
        disabled={sending}
        data-testid="contact-form-submit"
        className="px-4 py-2 bg-[#e11d2a] text-white rounded-md font-semibold text-sm disabled:opacity-50 hover:bg-[#c01825]"
      >
        {sending ? "Gönderiliyor..." : "Gönder"}
      </button>
    </form>
  );
}
